import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  createUser,
  grantGroup,
  INVITE_ROLES,
  listGrantableGroups,
  listPortalUsers,
  requestGroupAccess,
  revokeGroup,
  setUserEnabled,
  type CreateUserResult,
  type GrantableGroup,
  type InviteRole,
  type PortalUser,
} from "@/api/portal";
import { useIAMAuth } from "@/auth/iam";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Client Portal — users and privileges for the caller's own organization.
 *
 * The tenant is never chosen here; it comes from the JWT. We display it so an
 * admin can see *which* organization they are administering, but there is no
 * control to change it, and no request from this page carries an organization.
 *
 * **IAM is the enforcement point — this page renders its answers, including its
 * refusals.** OU containment, the closed grantable allow-list, last-ClientAdmin
 * protection and the no-unilateral-PHI-grant rule all live in IAM. We do not
 * pre-empt them with client-side rules that could drift out of agreement with
 * the thing that actually decides. Two consequences worth stating plainly:
 *
 * 1. **We do not infer "this group grants PHI" from its name.** IAM has not yet
 *    told us how it flags one (asked 2026-07-14), and a naming convention is not
 *    an authorization boundary. When an admin tries to grant a PHI-carrying
 *    group, IAM refuses with a reason, and we surface that refusal *as the next
 *    step* — a dialog that opens the access-request flow. The refusal is not an
 *    error state; it is the workflow.
 * 2. **An absent `groups` list renders as "unknown", not "none".** Whether IAM
 *    returns memberships on the list row is an open question. "This user has no
 *    roles" and "we were not told this user's roles" are different facts, and an
 *    admin who acts on the first when the second is true will grant a role
 *    someone already holds — or revoke one they need.
 */
/** Minimum reason length IAM accepts on an access request (docs#239 item 3). */
export const JUSTIFICATION_MIN_LENGTH = 10;

/** A lab admin group, `GG-SmartReports-<lab>-ClientAdmin`, in any case. */
function isClientAdminGroup(name: string | null): boolean {
  return name !== null && /^gg-smartreports-.+-clientadmin$/i.test(name);
}

export function PortalUsersPage(): JSX.Element {
  const { organizationId, user: me } = useIAMAuth();
  const selfId = me?.sub ?? null;
  const queryClient = useQueryClient();
  // Carries the GUID (what the request keys on) AND the name (what the admin
  // reads). A patient-data access request must never show only an opaque id.
  const [phiRequest, setPhiRequest] = useState<{
    user: PortalUser;
    groupId: string;
    groupName: string | null;
  } | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // docs#239 item 5: Disable asks first, naming the user.
  const [confirmDisable, setConfirmDisable] = useState<PortalUser | null>(null);
  const [inviteWarning, setInviteWarning] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ["portal-users"],
    queryFn: listPortalUsers,
    retry: false,
  });
  const groups = useQuery({
    queryKey: ["portal-groups"],
    queryFn: listGrantableGroups,
    retry: false,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["portal-users"] });
  };

  const grant = useMutation({
    mutationFn: ({ user, group }: { user: PortalUser; group: string }) =>
      grantGroup(user.id, group),
    onSuccess: invalidate,
    onError: (error: unknown, variables) => {
      // Defense-in-depth for the privilege RACE: a group that wasn't flagged
      // privileged when the list loaded but became so before the grant. IAM
      // rejects a direct add of a privileged (PHI) group with 409 (Aghiad
      // 2026-07-16), so a 409 here means "route to the request workflow", exactly
      // as the is_privileged flag does on the happy path. A genuine 403 (OU
      // containment, allow-list, last-ClientAdmin) is a real refusal and is shown
      // as an error, not sent into the PHI flow.
      if (isConflict(error)) {
        // Recover the readable name for the modal — variables.group is the GUID.
        const named = (groups.data ?? []).find((g) => g.group_id === variables.group);
        setPhiRequest({
          user: variables.user,
          groupId: variables.group,
          groupName: named?.group_name ?? null,
        });
      }
    },
  });

  // A PHI (privileged) group cannot be granted directly — IAM 409s it. Route it
  // to the request workflow by its is_privileged FLAG (Aghiad's "badge signal"),
  // before any doomed attempt; the onError 409 above is the fallback for a race.
  const onGrant = (user: PortalUser, groupId: string): void => {
    const g = (groups.data ?? []).find((x) => x.group_id === groupId);
    if (g?.is_privileged) {
      setPhiRequest({ user, groupId, groupName: g.group_name });
    } else {
      grant.mutate({ user, group: groupId });
    }
  };

  const revoke = useMutation({
    mutationFn: ({ user, group }: { user: PortalUser; group: string }) =>
      revokeGroup(user.id, group),
    onSuccess: invalidate,
  });

  const toggleEnabled = useMutation({
    mutationFn: (user: PortalUser) => setUserEnabled(user.id, !user.enabled),
    onSuccess: invalidate,
  });

  return (
    <AppLayout breadcrumbs={[{ label: t("portal.title") }, { label: t("portal.users") }]}>
      <Group justify="space-between" align="baseline">
        <Title order={1}>{t("portal.users")}</Title>
        <Group gap="md" align="baseline">
          <Text c="dimmed" size="sm">
            {t("portal.organization")}: <Code>{organizationId}</Code>
          </Text>
          <Button size="xs" onClick={() => setInviteOpen(true)}>
            {t("portal.invite_user")}
          </Button>
        </Group>
      </Group>
      <Text c="dimmed" mt="xs" size="sm">
        {t("portal.scope_note")}
      </Text>

      <MutationError grantError={grant.error} otherErrors={[revoke.error, toggleEnabled.error]} />

      {inviteWarning !== null ? (
        <Alert
          color="yellow"
          mt="md"
          title={t("portal.invite_role_not_granted_title")}
          withCloseButton
          onClose={() => setInviteWarning(null)}
          data-testid="invite-role-not-granted"
        >
          <Text size="sm">{inviteWarning}</Text>
        </Alert>
      ) : null}

      {users.isPending ? (
        <TableSkeleton label={t("portal.users_loading")} />
      ) : users.isError ? (
        <Alert color="red" title={t("portal.users_unavailable")} mt="md">
          <Text size="sm">
            {users.error instanceof Error ? users.error.message : t("authoring.error_generic")}
          </Text>
        </Alert>
      ) : (
        <UserTable
          users={users.data.users}
          grantable={groups.data ?? []}
          onGrant={onGrant}
          onRevoke={(user, group) => revoke.mutate({ user, group })}
          onToggleEnabled={(user) => {
            if (user.enabled) setConfirmDisable(user);
            else toggleEnabled.mutate(user);
          }}
          selfId={selfId}
          busy={grant.isPending || revoke.isPending || toggleEnabled.isPending}
        />
      )}

      <PhiRequestModal
        pending={phiRequest}
        onClose={() => setPhiRequest(null)}
        onSubmitted={invalidate}
      />

      <InviteUserModal
        opened={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={(result) => {
          setInviteWarning(
            result.role_granted === false ? (result.detail ?? t("authoring.error_generic")) : null,
          );
          invalidate();
        }}
      />

      <Modal
        opened={confirmDisable !== null}
        onClose={() => setConfirmDisable(null)}
        title={t("portal.disable_confirm_title")}
      >
        <Stack gap="sm">
          <Text size="sm">
            <Code>{confirmDisable === null ? "" : displayName(confirmDisable)}</Code>
          </Text>
          <Text size="sm">{t("portal.disable_confirm_body")}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDisable(null)}>
              {t("portal.cancel")}
            </Button>
            <Button
              color="orange"
              onClick={() => {
                if (confirmDisable !== null) toggleEnabled.mutate(confirmDisable);
                setConfirmDisable(null);
              }}
            >
              {t("portal.disable_confirm_submit")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppLayout>
  );
}

function displayName(user: PortalUser): string {
  return user.username ?? user.email ?? user.external_id ?? user.id;
}

function isConflict(error: unknown): boolean {
  return error instanceof Error && /409|conflict/i.test(error.message);
}

/**
 * Surface a failed mutation — a table that silently does not change is worse
 * than an error. The 409 exclusion is scoped to the GRANT mutation ONLY: there a
 * 409 is the privileged-group signal, routed to the access-request flow. A 409 on
 * REVOKE / disable-enable is a genuine conflict (e.g. the membership was already
 * lapsed) and MUST be shown — suppressing it re-introduces the "409 becomes
 * invisible" bug. A 403 (OU containment, allow-list, last ClientAdmin) is shown too.
 */
function MutationError({
  grantError,
  otherErrors,
}: {
  grantError: unknown;
  otherErrors: unknown[];
}): JSX.Element | null {
  const grantFailure =
    grantError instanceof Error && !isConflict(grantError) ? grantError : null;
  const otherFailure = otherErrors.find((e) => e instanceof Error) ?? null;
  const failed = grantFailure ?? otherFailure;
  if (!(failed instanceof Error)) return null;
  return (
    <Alert color="red" mt="md" title={t("portal.action_failed")}>
      <Text size="sm">{failed.message}</Text>
    </Alert>
  );
}

interface UserTableProps {
  users: PortalUser[];
  grantable: GrantableGroup[];
  onGrant: (user: PortalUser, group: string) => void;
  onRevoke: (user: PortalUser, group: string) => void;
  onToggleEnabled: (user: PortalUser) => void;
  busy: boolean;
  /** The caller's own subject. Their row gets no Disable and no × on their admin role. */
  selfId: string | null;
}

function UserTable({
  users,
  grantable,
  onGrant,
  onRevoke,
  onToggleEnabled,
  busy,
  selfId,
}: UserTableProps): JSX.Element {
  if (users.length === 0) {
    return <Text c="dimmed">{t("portal.users_empty")}</Text>;
  }
  return (
    <Table striped highlightOnHover withTableBorder mt="md">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("portal.col_user")}</Table.Th>
          <Table.Th>{t("portal.col_email")}</Table.Th>
          <Table.Th>{t("portal.col_status")}</Table.Th>
          <Table.Th>{t("portal.col_roles")}</Table.Th>
          <Table.Th>{t("portal.col_actions")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {users.map((user) => (
          <Table.Tr key={user.id}>
            <Table.Td>{user.username ?? user.external_id ?? user.id}</Table.Td>
            <Table.Td>{user.email ?? "—"}</Table.Td>
            <Table.Td>
              <Badge color={user.enabled ? "green" : "gray"} variant="light">
                {user.enabled ? t("portal.enabled") : t("portal.disabled")}
              </Badge>
            </Table.Td>
            <Table.Td>
              <RoleCell
                user={user}
                onRevoke={onRevoke}
                busy={busy}
                isSelf={selfId !== null && user.id === selfId}
              />
            </Table.Td>
            <Table.Td>
              <Group gap="xs" wrap="nowrap">
                <GrantControl user={user} grantable={grantable} onGrant={onGrant} busy={busy} />
                {/* docs#239 item 5: no Disable on your own row. The BFF refuses it too. */}
                {selfId !== null && user.id === selfId ? null : (
                  <Button
                    size="xs"
                    variant="light"
                    color={user.enabled ? "orange" : "green"}
                    disabled={busy}
                    onClick={() => onToggleEnabled(user)}
                  >
                    {user.enabled ? t("portal.action_disable") : t("portal.action_enable")}
                  </Button>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

/**
 * The roles a user holds — or an honest "unknown".
 *
 * An empty array from IAM is ambiguous today (see the module docstring): it may
 * mean "no roles", or "memberships are not on this row". We say so rather than
 * render a confident blank.
 */
function RoleCell({
  user,
  onRevoke,
  busy,
  isSelf,
}: {
  user: PortalUser;
  onRevoke: (user: PortalUser, groupId: string) => void;
  busy: boolean;
  /** The caller's own row: their lab admin role shows without a ×. */
  isSelf: boolean;
}): JSX.Element {
  // `null` = IAM wasn't asked for memberships (should not happen — we always
  // ?expand=groups — but is NOT the same as "no roles"). `[]` = asked, none held.
  if (user.groups === null) {
    return (
      <Tooltip label={t("portal.roles_unknown_detail")} multiline w={260}>
        <Text c="dimmed" size="sm">
          {t("portal.roles_unknown")}
        </Text>
      </Tooltip>
    );
  }
  // A membership with an `expires_at` in the past has LAPSED — IAM's resolver
  // already ignores it, so the user does not hold it. Rendering it as a live
  // badge would tell an admin the user still has (often PHI) access they don't,
  // driving a wrong access decision. Show only what's held right now; a user
  // whose every role has expired correctly reads as "no roles".
  const active = user.groups.filter(
    (m) => m.expires_at === null || new Date(m.expires_at).getTime() > Date.now(),
  );
  if (active.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t("portal.roles_none")}
      </Text>
    );
  }
  return (
    <Group gap="xs">
      {active.map((membership) => (
        <Badge
          key={membership.group_id}
          variant="light"
          rightSection={
            isSelf && isClientAdminGroup(membership.group_name) ? undefined : (
              <ActionIcon
                size="xs"
                variant="transparent"
                color="gray"
                disabled={busy}
                aria-label={`${t("portal.action_revoke")} ${membership.group_name ?? membership.group_id}`}
                onClick={() => onRevoke(user, membership.group_id)}
              >
                ×
              </ActionIcon>
            )
          }
        >
          {membership.group_name ?? membership.group_id}
        </Badge>
      ))}
    </Group>
  );
}

/** Pick a group from IAM's allow-list and grant it. */
function GrantControl({
  user,
  grantable,
  onGrant,
  busy,
}: {
  user: PortalUser;
  grantable: GrantableGroup[];
  onGrant: (user: PortalUser, group: string) => void;
  busy: boolean;
}): JSX.Element {
  const [group, setGroup] = useState<string | null>(null);
  const options = grantable.map((g) => ({
    // Value is the GUID (what grant/revoke key on); label is the readable name.
    value: g.group_id,
    // A PHI-carrying (privileged) group is LABELLED so the admin knows it routes
    // through the approval workflow — but not hidden or disabled here. IAM's flag
    // (is_privileged), not our name-sniff; and even if it's wrong, a direct grant
    // is refused server-side and the admin lands in the request flow. Hint, not guard.
    label: g.is_privileged
      ? `${g.group_name ?? g.group_id} — ${t("portal.group_phi_suffix")}`
      : (g.group_name ?? g.group_id),
  }));

  return (
    <Group gap="xs" wrap="nowrap">
      <Select
        size="xs"
        w={230}
        placeholder={t("portal.grant_placeholder")}
        aria-label={t("portal.grant_placeholder")}
        data={options}
        value={group}
        onChange={setGroup}
        disabled={busy || options.length === 0}
        searchable
      />
      <Button
        size="xs"
        variant="light"
        disabled={busy || group === null}
        onClick={() => {
          if (group !== null) onGrant(user, group);
        }}
      >
        {t("portal.action_grant")}
      </Button>
    </Group>
  );
}

/**
 * The access-request flow — opened when IAM refuses a direct grant.
 *
 * This is the ONLY route to PHI access (§4.3: no unilateral PHI grants),
 * ClientAdmins included. Approval needs a second person (IAM enforces approver ≠
 * requester) and grants a membership that expires on its own.
 */
function PhiRequestModal({
  pending,
  onClose,
  onSubmitted,
}: {
  pending: { user: PortalUser; groupId: string; groupName: string | null } | null;
  onClose: () => void;
  onSubmitted: () => void;
}): JSX.Element {
  const [justification, setJustification] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      requestGroupAccess({
        targetUserId: pending?.user.id ?? "",
        groupId: pending?.groupId ?? "",
        justification: justification.trim(),
      }),
    onSuccess: () => {
      setJustification("");
      onSubmitted();
      onClose();
    },
  });

  return (
    <Modal opened={pending !== null} onClose={onClose} title={t("portal.phi_request_title")}>
      <Stack gap="sm">
        <Text size="sm">{t("portal.phi_request_body")}</Text>
        <Text size="sm">
          <Code>{pending?.groupName ?? pending?.groupId}</Code> →{" "}
          <Code>{pending?.user.username ?? pending?.user.id}</Code>
        </Text>
        <Textarea
          label={t("portal.phi_request_justification")}
          description={`${t("portal.phi_request_justification_hint")} ${t("portal.phi_request_min_hint")}`}
          value={justification}
          onChange={(event) => setJustification(event.currentTarget.value)}
          autosize
          minRows={3}
        />
        {submit.isError ? (
          <Alert color="red">
            <Text size="sm">
              {submit.error instanceof Error ? submit.error.message : t("authoring.error_generic")}
            </Text>
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("portal.cancel")}
          </Button>
          <Button
            disabled={justification.trim().length < JUSTIFICATION_MIN_LENGTH || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {t("portal.phi_request_submit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Invite a new user into the caller's own organization.
 *
 * The org is not asked for, IAM binds the new user to the caller's tenant from
 * the token. The picker is a FIXED list that the BFF maps onto IAM (see
 * `INVITE_ROLES`). The failure paths are distinct on purpose: 409 = the
 * username/email is taken (pick another), 400 = a field IAM rejected, 503 = invite
 * is switched off for this portal. The detail is a fixed reason per status, never
 * IAM's own text, which can echo what was sent (docs#171). A 201 with
 * `role_granted: false` means the invite went out without the role, and the page
 * says so.
 */
function InviteUserModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (result: CreateUserResult) => void;
}): JSX.Element {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const reset = (): void => {
    setUsername("");
    setEmail("");
    setRole(null);
    setFirstName("");
    setLastName("");
  };

  const submit = useMutation({
    mutationFn: () =>
      createUser({
        username: username.trim(),
        email: email.trim(),
        // Guarded by `ready` below — the button is disabled until role is set.
        role: role as InviteRole,
        ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      }),
    onSuccess: (result) => {
      onCreated(result);
      reset();
      onClose();
    },
  });

  // Every non-success close path clears the form AND the last error. The modal
  // component stays mounted (only its body is hidden when closed), so without this
  // a failed invite — "username already exists", the typed fields — would still be
  // showing when the admin reopens to add a *different* person, reading as "that
  // user already exists" and inviting an accidental duplicate submit of stale
  // values. (On success the fields are already cleared above.)
  const handleClose = (): void => {
    reset();
    submit.reset();
    onClose();
  };

  // A minimal, honest client check: a reachable-looking email + a username + a
  // role. Deeper validation is IAM's — we do not reimplement its rules here, only
  // avoid firing an obviously-incomplete request.
  const ready =
    username.trim().length > 0 && /.+@.+\..+/.test(email.trim()) && role !== null;

  return (
    <Modal opened={opened} onClose={handleClose} title={t("portal.invite_title")}>
      <Stack gap="sm">
        <Text size="sm">{t("portal.invite_body")}</Text>
        <TextInput
          label={t("portal.invite_username")}
          value={username}
          onChange={(event) => setUsername(event.currentTarget.value)}
          required
        />
        <TextInput
          label={t("portal.invite_email")}
          description={t("portal.invite_email_hint")}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          required
        />
        <Select
          label={t("portal.invite_role")}
          description={t("portal.invite_role_hint")}
          placeholder={t("portal.invite_role_placeholder")}
          data={INVITE_ROLES.map((r) => ({ value: r, label: r }))}
          value={role}
          onChange={(value) => setRole(value as InviteRole | null)}
          required
        />
        <Group grow>
          <TextInput
            label={t("portal.invite_first_name")}
            value={firstName}
            onChange={(event) => setFirstName(event.currentTarget.value)}
          />
          <TextInput
            label={t("portal.invite_last_name")}
            value={lastName}
            onChange={(event) => setLastName(event.currentTarget.value)}
          />
        </Group>
        {submit.isError ? (
          <Alert color="red">
            <Text size="sm">
              {submit.error instanceof Error ? submit.error.message : t("authoring.error_generic")}
            </Text>
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            {t("portal.cancel")}
          </Button>
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {t("portal.invite_submit")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
